import { createContext, memo, type ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateWorkspaceAgentPillAssetCovers } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillAssetCovers';
import { usePrivateWorkspaceAgentPillClips } from '~/components/v2/components/workspace/workspace-agent-panel/queries/usePrivateWorkspaceAgentPillClips';
import { type WorkspaceAgentMessagePill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { flattenWorkspaceAgentAssetPillItems } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';
import { isWorkspaceAgentClipLookupUnresolved } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentClipLookupUnresolved';
import {
  replaceRegisteredWorkspaceAgentClipIds,
  replaceRegisteredWorkspaceAgentKeyedList,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/replaceRegisteredWorkspaceAgentClipIds';
import { resolveWorkspaceAgentAssetPillClip } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentAssetPillClip';
import { type WorkspaceAgentPillClip } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentPillClipsById';
import { isWorkspaceAgentDirectAssetMonikerPill } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentDirectAssetMoniker';
import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PrivateWorkspaceAgentPillClipsApi = {
  /** Cover clip id for an `@[asset:<assetId>]` pill, once that cover has loaded. */
  getDirectAssetClipId: (pill: WorkspaceAgentMessagePill | undefined) => string | undefined;
  getPillClip: (pill: WorkspaceAgentMessagePill | undefined) => WorkspaceAgentPillClip | undefined;
  getPillClipById: (clipId: string) => WorkspaceAgentPillClip | undefined;
  /**
   * This id has no successful `Clips.listByIds` coverage because the lookup failed.
   * An id a successful response already omitted is confirmed missing, so this is false.
   */
  isClipLookupFailed: (clipId: string) => boolean;
  /** Version ids from artifact rows that were not on the history pills when this batch was built. */
  registerClipIds: (pillId: string, clipIds: readonly string[]) => void;
  /**
   * Asset pills that were not on the history batch — elicitation prompts synthesize them from
   * `@[asset:…]` / `@[assetVersion:…]` instead of `metadata.pills`.
   */
  registerPills: (pillId: string, pills: readonly WorkspaceAgentMessagePill[]) => void;
  /** Asset ids named by `@[asset:<assetId>]` cells in a table artifact, which are not on any history pill. */
  registerDirectAssetIds: (key: string, assetIds: readonly string[]) => void;
  /** In flight with nothing yet, so a chip can wait instead of declaring the asset gone. */
  isPending: boolean;
  /** A wider batch is in flight over the previous one's results, so an unknown id is not yet missing. */
  isRefreshing: boolean;
};

type CoveredClipLookup = {
  clipIds: ReadonlySet<string>;
  clipsById: ReadonlyMap<string, WorkspaceAgentPillClip>;
};

const defaultPillClipsValue: PrivateWorkspaceAgentPillClipsApi = {
  getDirectAssetClipId: () => undefined,
  getPillClip: () => undefined,
  getPillClipById: () => undefined,
  isClipLookupFailed: () => false,
  registerClipIds: () => undefined,
  registerPills: () => undefined,
  registerDirectAssetIds: () => undefined,
  isPending: false,
  isRefreshing: false,
};

export const PrivateWorkspaceAgentPillClipsContext =
  createContext<PrivateWorkspaceAgentPillClipsApi>(defaultPillClipsValue);

export type PrivateWorkspaceAgentPillClipsProviderProps = {
  children: ReactNode;
  /** Parsed once by the panel provider. */
  pills: readonly WorkspaceAgentMessagePill[];
  workspaceId: string;
};

/**
 * Resolves every asset pill in the loaded history, so chips can show a thumbnail and name rather
 * than a truncated id. The shared lookup is split at the `listByIds` id cap. Lives above the message
 * list because the chips are rendered from a module-scope markdown component map and so cannot be
 * reached by props.
 */
export const PrivateWorkspaceAgentPillClipsProvider = memo(
  ({ children, pills, workspaceId }: PrivateWorkspaceAgentPillClipsProviderProps) => {
    const [registeredClipIds, setRegisteredClipIds] = useState<ReadonlyMap<string, readonly string[]>>(() => new Map());
    const [registeredPills, setRegisteredPills] = useState<ReadonlyMap<string, readonly WorkspaceAgentMessagePill[]>>(
      () => new Map(),
    );
    const registerClipIds = useCallback((pillId: string, clipIds: readonly string[]) => {
      setRegisteredClipIds((current) => replaceRegisteredWorkspaceAgentClipIds(current, pillId, clipIds));
    }, []);
    const registerPills = useCallback((pillId: string, extraPills: readonly WorkspaceAgentMessagePill[]) => {
      setRegisteredPills((current) => replaceRegisteredWorkspaceAgentKeyedList(current, pillId, extraPills));
    }, []);
    const [registeredAssetIds, setRegisteredAssetIds] = useState<ReadonlyMap<string, readonly string[]>>(
      () => new Map(),
    );
    const registerDirectAssetIds = useCallback((key: string, assetIds: readonly string[]) => {
      setRegisteredAssetIds((current) => replaceRegisteredWorkspaceAgentKeyedList(current, key, assetIds));
    }, []);
    const extraPills = useMemo(() => [...registeredPills.values()].flat(), [registeredPills]);
    const resolvedPills = extraPills.length === 0 ? pills : [...pills, ...extraPills];
    const historyClipIds = useMemo(
      () =>
        flattenWorkspaceAgentAssetPillItems(resolvedPills)
          .map((item) => item.clipId)
          .filter((id): id is string => !!id),
      [resolvedPills],
    );
    const clipIds = useMemo(() => {
      const extraClipIds = [...registeredClipIds.values()].flat();

      return extraClipIds.length === 0 ? historyClipIds : [...historyClipIds, ...extraClipIds];
    }, [historyClipIds, registeredClipIds]);
    const directAssetIds = useMemo(() => {
      const historyAssetIds = resolvedPills.flatMap((pill) =>
        pill.type === 'asset' && isWorkspaceAgentDirectAssetMonikerPill(pill) ? [pill.assetId] : [],
      );
      const extraAssetIds = [...registeredAssetIds.values()].flat();

      return extraAssetIds.length === 0 ? historyAssetIds : [...historyAssetIds, ...extraAssetIds];
    }, [resolvedPills, registeredAssetIds]);

    const {
      data: pillClipsById,
      fetchStatus,
      isError,
      isPending,
      isPlaceholderData,
    } = usePrivateWorkspaceAgentPillClips({ workspaceId, clipIds });
    const {
      data: coversByAssetId,
      fetchStatus: coverFetchStatus,
      isPending: isCoverPending,
      isPlaceholderData: isCoverPlaceholderData,
    } = usePrivateWorkspaceAgentPillAssetCovers({ assetIds: directAssetIds, workspaceId });
    const coveredLookupRef = useRef<CoveredClipLookup | undefined>(undefined);
    const requestedClipIds = useMemo(() => new Set(clipIds), [clipIds]);

    // Remember the last successful batch. A later error (refetch, or a wider batch over the 250 cap)
    // must not forget which ids that response already omitted.
    if (pillClipsById && !isError && !isPlaceholderData) {
      coveredLookupRef.current = { clipIds: requestedClipIds, clipsById: pillClipsById };
    }

    const coveredClipIds = pillClipsById && !isPlaceholderData ? requestedClipIds : coveredLookupRef.current?.clipIds;
    const clipsById = pillClipsById ?? (isError ? coveredLookupRef.current?.clipsById : undefined);
    const isClipLookupFailed = useCallback(
      (clipId: string) => isWorkspaceAgentClipLookupUnresolved(clipId, { coveredClipIds, isError }),
      [coveredClipIds, isError],
    );

    const getPillClipById = useCallback((clipId: string) => clipsById?.get(clipId), [clipsById]);

    const getPillClip = useCallback(
      (pill: WorkspaceAgentMessagePill | undefined) =>
        resolveWorkspaceAgentAssetPillClip({ clipsById, coversByAssetId, pill }).clip,
      [clipsById, coversByAssetId],
    );

    const getDirectAssetClipId = useCallback(
      (pill: WorkspaceAgentMessagePill | undefined) =>
        pill?.type === 'asset' && pill.versionId === undefined
          ? resolveWorkspaceAgentAssetPillClip({ clipsById, coversByAssetId, pill }).clipId
          : undefined,
      [clipsById, coversByAssetId],
    );

    // A disabled query (no ids) is `isPending` forever; `fetchStatus` separates loading from never asked.
    const isResolving = (isPending && fetchStatus !== 'idle') || (isCoverPending && coverFetchStatus !== 'idle');
    // Placeholder data with nothing in flight is final, not a refresh: a query disabled after data
    // existed would otherwise leave ids it does not carry pending forever.
    const isRefreshing =
      (isPlaceholderData && fetchStatus !== 'idle') || (isCoverPlaceholderData && coverFetchStatus !== 'idle');

    const contextValue = useMemo(
      () => ({
        getDirectAssetClipId,
        getPillClip,
        getPillClipById,
        isClipLookupFailed,
        isPending: isResolving,
        isRefreshing,
        registerClipIds,
        registerDirectAssetIds,
        registerPills,
      }),
      [
        getDirectAssetClipId,
        getPillClip,
        getPillClipById,
        isClipLookupFailed,
        isRefreshing,
        isResolving,
        registerClipIds,
        registerDirectAssetIds,
        registerPills,
      ],
    );

    return (
      <PrivateWorkspaceAgentPillClipsContext.Provider value={contextValue}>
        {children}
      </PrivateWorkspaceAgentPillClipsContext.Provider>
    );
  },
);

PrivateWorkspaceAgentPillClipsProvider.displayName = 'PrivateWorkspaceAgentPillClipsProvider';

export const usePrivateWorkspaceAgentPanelPillClips = () => {
  const context = useContext(PrivateWorkspaceAgentPillClipsContext);

  if (context === defaultPillClipsValue) {
    const error = new Error(
      'usePrivateWorkspaceAgentPanelPillClips must be used within PrivateWorkspaceAgentPillClipsProvider',
    );

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Workspace agent pill clips context not found' });
  }

  return context;
};
